import { useState } from 'react';

import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';


import useRoles from 'hooks/useRoles';
import useFHRequestMessage from 'hooks/useFHRequestMessage';
import useFHRequest from 'hooks/useFHRequest';
import useWorkKey from 'hooks/useWorkKey';
import useClock from 'hooks/useClock';
import Error from 'components/Error';
import Success from 'components/Success';
import LockedFeature from 'components/LockedFeature';
import RequestInfoCloseQuietly from 'components/RequestInfoCloseQuietly';
import { FHRequestType } from 'stores/fhrequest_store';
import getIcon from 'utils/getIcon';
import { ClockInButton } from 'components/ClockHeader';
import { TeamSelector } from 'components/CollabInviteFeature';
import { RequestSidebarAssigner } from './RequestSidebar';

function NeedsAttention({ request, setEditing, editing, clockedIn }: { request: FHRequestType, setEditing: (editing: string) => void, editing: boolean, clockedIn: boolean }) {
    const { supportHelpRespond } = useFHRequestMessage();
    const workKey = useWorkKey();
    const { currentClock } = useClock();
    const [message, setMessage] = useState('');

    const [sending, setSending] = useState(false);
    const EditIcon = editing ? getIcon('Close') : getIcon('Edit');
    function sendMessage() {
        if (message === '') {
            Error("No message?");
            return;
        }
        if (workKey && currentClock) {
            setSending(true);
            supportHelpRespond(workKey,
                request.uuid,
                message,
                currentClock.uuid)
                .then(() => {
                    setSending(false);
                    setMessage('');
                    setEditing('');
                });
        }

    }
    if (request.needs_manager_attention) {
        return (
            <Stack
                sx={{ spacing: 2, py: 2 }}
                alignItems="center">
                <Stack alignItems="center">
                    <Typography
                        endDecorator={
                            (clockedIn && editing) &&
                            <EditIcon
                                onClick={() => setEditing('')}
                                sx={{ cursor: 'pointer' }}
                            />
                        }
                        color="warning" level="title-lg">
                        Support Team
                    </Typography>
                    {!clockedIn &&
                        <Typography
                            color="warning"
                            level="body-sm">Clock in to respond</Typography>}
                    {(clockedIn && !editing) &&
                        <Stack spacing={1}>
                            <Typography level="body-xs">
                                This request needs support's attention
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setEditing('support')
                                }}>Respond</Button>
                        </Stack>
                    }
                </Stack>
                {sending &&
                    <Typography level="body-xs">Sending...</Typography>
                }
                {(editing && !sending) &&
                    <Stack spacing={1}>
                        <Textarea
                            disabled={!editing}
                            minRows={5}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add notes"
                            sx={{ width: '100%' }} />
                        <Button
                            onClick={() => sendMessage()}
                        >Send</Button>
                    </Stack>
                }

            </Stack>
        );
    }
    else {
        return (
            <Stack alignItems="center" m={1}>
                <Typography level="title-sm">
                    Does not need support
                </Typography>
            </Stack>
        );
    }

}

function ArchiveRequest({ request }: { request: FHRequestType }) {
    const { supportArchiveRequest } = useFHRequest();
    const workKey = useWorkKey();
    const { currentClock } = useClock();
    const [archiving, setArchiving] = useState(false);

    function archiveRequest() {
        if (workKey) {
            setArchiving(true);
            supportArchiveRequest(workKey,
                request.key) // key to suport old ones
                .then(() => {
                    setArchiving(false);
                    Success("Archived");
                });
        }
    }

    if (request.archived) {
        return (
            <Stack direction="row"
                justifyContent="center">
                <Typography level="body-xs">This request is archived.</Typography>
            </Stack>
        );
    }


    if (currentClock?.associated_uuid && (currentClock?.associated_uuid != request.uuid)) {
        return (
            <Stack direction="row"
                justifyContent="center">
                <Typography level="body-xs">Can't archive while clocked in elsewhere.</Typography>
            </Stack>
        );
    }

    return (
        <LockedFeature
            color="success"
            copy="Archive"
        >
        <Stack
             m={1}
                spacing={1}
                alignItems="center"
                justifyContent="center"

            >
                <Typography
                    color="danger"
                    level="title-md">
                    Archive this request?
                </Typography>
                <Typography level="body-xs">
                    We'll ignore new email to this request which should stop it from re-opening.
                </Typography>
                <Button
                    variant="outlined"
                    size="sm"
                    color="danger"
                    loading={archiving}
                    onClick={() => archiveRequest()}
                >Archive</Button>
                {archiving &&
                    <Typography level="body-xs">Archiving...</Typography>
                }
            </Stack>
        </LockedFeature>
    );
}

function RequestSidebarSupportTeam({ request, setEditing, editing, clockedIn }: { request: FHRequestType, setEditing: (editing: string) => void, editing: boolean, clockedIn: boolean }) {
    const { hasRole } = useRoles();
    const [_, setAssigning] = useState(false);

    if (!hasRole('support')) {
        return null;
    }

    return (
        <Stack spacing={1}>
            <NeedsAttention request={request} setEditing={setEditing} editing={editing} clockedIn={clockedIn} />

            <LockedFeature
                copy="Assign Assistant"
                lockedIconName="AssignmentInd"
                color="success"
                outlined={false}
            >
                <RequestSidebarAssigner
                    request={request}
                    setEditing={setAssigning} />
            </LockedFeature>

            <LockedFeature
                copy="Invite Assistant"
                lockedIconName="Send"
                color="success"
                outlined={false}
            >

                <TeamSelector ignoreIdentities={[]} associated_type='request' associated_uuid={request.uuid} />
            </LockedFeature>

            <LockedFeature
                copy="Close Quietly"
                lockedIconName="Close"
                color="success"
                outlined={false}
            >
                <RequestInfoCloseQuietly
                    request={request} />

            </LockedFeature>
            <ArchiveRequest request={request} />
            {!clockedIn &&
                <ClockInButton variant="plain" request={request} />
            }

        </Stack>
    );
}
export default RequestSidebarSupportTeam;
